'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'

import ProductInfoPriceDS from '@src/components/blocks/product-info-price-ds/product-info-price-ds'
import { ProductNameDS } from '@src/components/blocks/product-name-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { PurchaseType } from '@src/quince-components/src/atoms/purchaseTypeSelector'
import { getProductLink } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import Product from '@src/quince-site-common/src/domains/Product'
import { BaseProductVariant, ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { THIRD_PARTY } from '@src/quince-site-common/src/helpers/productHelper'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { getIsFinalSaleProduct } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

const nameBlockCss = css({
  marginBottom: { base: 'space-20', lg: 'space-24' },
})

export const MiniPDPProductInfoDS = ({
  product,
  selectedOptions,
  selectedVariant,
  brandName,
  brandLink,
  formattedSalePrice,
  formattedTraditionalPrice,
  savingPercentage,
  formattedFestivalOfferPrice,
  festivalPricePerUnit,
  formattedSubscriptionPrice,
  subscriptionOfferPricePerUnit,
  purchaseOption,
}: {
  product: Product
  selectedOptions: ProductVariantOption[]
  selectedVariant?: BaseProductVariant
  brandName?: string
  brandLink?: string
  formattedSalePrice: string
  formattedTraditionalPrice?: string
  savingPercentage?: number
  formattedFestivalOfferPrice?: string
  festivalPricePerUnit?: number
  formattedSubscriptionPrice?: string
  subscriptionOfferPricePerUnit?: number
  purchaseOption: PurchaseType
}) => {
  const router = useRouter()

  const avgRating = product.productReviewSummary?.averageRating ?? 0
  const reviewCount = product.productReviewSummary?.reviewCount ?? 0

  const productLink = getProductLink(product, selectedOptions)
  const handleReviewClick = () => {
    router.push(productLink)
    pdpStateManager.setShowReviewSection({ reviewSectionInFocus: true })
    trackEvent(TELEMETRY_EVENT_NAMES.ENTER_FEEDBACK, {
      context: {
        productProperties: getProductPropertiesForTracking(product, selectedVariant?.spid?.toString()),
      },
    })
  }

  const hideSavings = product?.hideSavings ?? false
  const isFinalSaleProduct = getIsFinalSaleProduct(product, selectedOptions)
  const isVariantThirdParty = selectedVariant?.vendorType === THIRD_PARTY

  return (
    <div data-testid={TEST_LOCATORS.MINI_PDP.PRODUCT_INFO}>
      <ProductNameDS
        variant="ratingSide"
        className={nameBlockCss}
        productTitle={product.title}
        brandName={brandName}
        brandLink={brandLink}
        avgRating={avgRating}
        reviewCount={reviewCount}
        onReviewSummaryClick={handleReviewClick}
      />
      <ProductInfoPriceDS
        product={product}
        formattedSubscriptionPrice={formattedSubscriptionPrice}
        formattedSalePrice={formattedSalePrice}
        formattedTraditionalPrice={formattedTraditionalPrice}
        savingPercentage={savingPercentage}
        formattedFestivalOfferPrice={formattedFestivalOfferPrice}
        brandName={brandName}
        subscriptionOfferPricePerUnit={subscriptionOfferPricePerUnit}
        pricePerServing={selectedVariant?.pricePerServing}
        traditionalPricePerServing={selectedVariant?.traditionalPricePerServing}
        festivalPricePerUnit={festivalPricePerUnit}
        hideSavings={hideSavings}
        isFinalSaleProduct={isFinalSaleProduct}
        purchaseOption={purchaseOption}
        updatedDesignForV2={true}
        isVariantThirdParty={isVariantThirdParty}
        selectedOptions={selectedOptions}
      />
    </div>
  )
}
